import { Link } from "react-router-dom";
import { ArrowRight, Leaf } from "lucide-react";
import {
  IMG,
  STEWARDSHIP_INTRO,
  SEED_CARRIES,
  SEED_STEWARDSHIP_ACTIVITIES,
  CLIMATE_SEED_SUPPORTS,
  STEWARDSHIP_OUTCOMES,
} from "../data/content";
import Seo from "../components/Seo";

export default function Stewardship() {
  return (
    <div data-testid="page-stewardship">
      <Seo
        title="Native Seed Stewardship"
        description="Stewardship begins with seed. SeedKeepers strengthens expert-led stewardship of Country through community seed banks, seed systems, climate adaptation and the long-term care of future generations."
        path="/stewardship"
        image={IMG.handsSeeds}
        type="article"
      />

      {/* ---------- Hero ---------- */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <p className="specimen-label mb-6" data-testid="stewardship-label">
              SeedKeepers · Thought Leadership · Caring for Country
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[84px] leading-[0.95] tracking-tight">
              Stewardship begins<br />
              <span className="italic text-eucalypt">with seed.</span>
            </h1>
            <p className="mt-8 text-base md:text-lg max-w-2xl text-ink/85 leading-relaxed">
              {STEWARDSHIP_INTRO}
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="polaroid">
              <img
                src={IMG.handsSeeds}
                alt="Hands holding native seed on Country"
                className="w-full h-[420px] md:h-[480px] object-cover"
              />
            </div>
            <p className="mt-3 text-xs text-muted italic">
              Stewardship held in the hand — Country, knowledge and seed.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- Why Seed Matters / Seed is infrastructure ---------- */}
      <section className="border-b border-rule bg-bark text-chalk">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <p className="specimen-label text-chalk/60 mb-3">01 — Why Seed Matters</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
              <em className="text-ochre">Seed is infrastructure.</em>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base md:text-lg text-chalk/80 leading-relaxed">
              At SeedKeepers, we see seed differently. Every resilient landscape
              begins with seed — it is the foundation that restoration,
              biodiversity and future generations depend upon.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- Seed carries ---------- */}
      <section className="border-b border-rule bg-parchment/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-5">
            <p className="section-num mb-3">02 — What Seed Carries</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
              Seed carries more than potential to grow.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid grid-cols-1 gap-3" data-testid="stewardship-seed-carries">
              {SEED_CARRIES.map((s, i) => (
                <li key={s} className="flex items-baseline gap-4 border-b border-rule pb-3">
                  <span className="font-mono text-[10px] tracking-[0.22em] text-muted">No. 0{i + 1}</span>
                  <span className="font-serif text-2xl md:text-3xl">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ---------- Community Seed Banks ---------- */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <img
              src={IMG.containerSeedBank}
              alt="Community seed bank inside a converted container"
              className="w-full h-[460px] object-cover border border-rule p-2 bg-cream"
            />
            <p className="mt-3 text-xs text-muted italic">
              A community seed bank — stewardship infrastructure for future generations.
            </p>
          </div>
          <div className="lg:col-span-7">
            <p className="section-num mb-3">03 — Community Seed Banks</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
              Stewardship infrastructure for <em className="text-eucalypt">future generations.</em>
            </h2>
            <p className="mt-8 text-base md:text-lg text-ink/85 leading-relaxed max-w-2xl">
              Community seed banks are more than storage facilities. They are
              places where local species are safeguarded, traditional knowledge
              is maintained, climate resilience is strengthened and future
              restoration capacity is secured.
            </p>
            <p className="mt-6 font-serif italic text-2xl md:text-3xl text-eucalypt leading-snug max-w-2xl">
              A community seed bank is an investment in future generations.
            </p>
            <p className="mt-6 text-base md:text-lg text-ink/85 leading-relaxed max-w-2xl">
              It ensures that communities retain access to the genetic resources
              needed to respond to environmental change.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- Seed Stewardship: Caring for Country ---------- */}
      <section className="border-b border-rule bg-parchment/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-7">
              <p className="section-num mb-3">04 — Seed Stewardship</p>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
                Caring for Country <em className="text-eucalypt">through seed.</em>
              </h2>
            </div>
            <p className="lg:col-span-5 text-base md:text-lg text-ink/85 leading-relaxed self-end">
              By strengthening seed stewardship, communities strengthen their
              ability to care for Country across generations.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-rule border border-rule" data-testid="stewardship-activities">
            {SEED_STEWARDSHIP_ACTIVITIES.map((a, i) => (
              <div key={a} className="bg-card p-7 flex items-start gap-4">
                <span className="font-serif text-4xl text-ochre2 leading-none flex-shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="font-serif text-xl md:text-2xl leading-tight">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Climate Adaptation Through Seed ---------- */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <p className="section-num mb-3">05 — Climate Adaptation</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
              One of the most practical climate adaptation tools available to communities.
            </h2>
            <p className="mt-8 text-base md:text-lg text-ink/85 leading-relaxed max-w-2xl">
              As climate conditions change, communities need access to locally
              adapted seed — and the knowledge to use it effectively. Strong seed
              systems make that possible.
            </p>
            <p className="specimen-label mt-10 mb-5">Seed systems support</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="stewardship-climate-supports">
              {CLIMATE_SEED_SUPPORTS.map((c) => (
                <li key={c} className="flex items-center gap-3 border-b border-rule pb-2">
                  <Leaf size={15} className="text-eucalypt flex-shrink-0" />
                  <span className="text-base md:text-lg text-ink/85">{c}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <img
              src={IMG.acaciaPods}
              alt="Locally adapted native seed pods"
              className="w-full h-[480px] object-cover border border-rule p-2 bg-cream"
            />
          </div>
        </div>
      </section>

      {/* ---------- Stewardship Outcomes ---------- */}
      <section className="border-b border-rule bg-bark text-chalk">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="specimen-label text-chalk/60 mb-3">06 — Stewardship Outcomes</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-4 max-w-3xl leading-[1.02]">
            Success, measured through four <em className="text-ochre">interconnected outcomes.</em>
          </h2>
          <p className="text-chalk/70 max-w-2xl mb-14 text-sm md:text-base leading-relaxed">
            Stewardship is not a single metric. We measure it across the natural,
            cultural, social and economic life of community and Country.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-chalk/15" data-testid="stewardship-outcomes">
            {STEWARDSHIP_OUTCOMES.map((o) => (
              <div key={o.label} className="bg-bark p-8 flex flex-col gap-4" data-testid={`stewardship-outcome-${o.label.toLowerCase()}`}>
                <span className="specimen-label text-chalk/60">No. {o.no}</span>
                <h3 className="font-serif text-3xl md:text-4xl">{o.label}</h3>
                <p className="text-sm text-chalk/75 leading-relaxed">{o.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Closing ---------- */}
      <section>
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
          <p className="specimen-label mb-8">Stewardship through seed</p>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.05]">
            Strengthen the systems that let communities <span className="italic text-eucalypt">care for Country.</span>
          </h2>
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="stewardship-contact-cta"
              className="inline-flex items-center justify-center gap-3 bg-eucalypt text-chalk px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-eucalypt-dark rounded-full hover:-translate-y-0.5 transition-all"
            >
              Talk to us <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
